import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Printer } from "lucide-react";
import { TRANSPORT } from "@/components/equipment/HireBillingDialog";
import { fmtDate, localToday } from "@/components/equipment/equipmentMeta";
import { EquipmentThumb } from "@/components/equipment/EquipmentPhotos";

const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const Block = ({ label, children, testid }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">{label}</div>
    <div className="text-sm text-zinc-900 whitespace-pre-wrap break-words" data-testid={testid}>{children || "—"}</div>
  </div>
);

const SignLine = ({ label }) => (
  <div className="space-y-6">
    <div className="text-xs font-bold uppercase tracking-wider text-zinc-700">{label}</div>
    {["Name", "Signature", "Date"].map((f) => (
      <div key={f} className="flex items-end gap-3">
        <span className="text-xs text-zinc-500 w-20">{f}</span>
        <span className="flex-1 border-b border-zinc-400 h-5" />
      </div>
    ))}
  </div>
);

export default function HireDocket() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/hire-orders/${id}/docket`).then((r) => setD(r.data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [id]);

  if (error) return <div className="p-10 text-red-600" data-testid="docket-error">{error}</div>;
  if (!d) return <div className="p-10 text-zinc-500 font-mono text-sm">Loading…</div>;
  const { order: o, equipment: e, tenant: t } = d;
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white py-8 print:py-0" data-testid="hire-docket">
      <div className="max-w-[800px] mx-auto mb-4 flex justify-end print:hidden">
        <button onClick={() => window.print()} data-testid="docket-print-button"
          className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2">
          <Printer size={16} /> Print / Save as PDF
        </button>
      </div>
      <div className="max-w-[800px] mx-auto bg-white p-10 shadow-[0_4px_16px_rgba(0,0,0,0.08)] print:shadow-none print:p-0 space-y-8">
        <div className="flex items-start justify-between gap-6 border-b-2 border-zinc-900 pb-5">
          <div className="flex items-center gap-4">
            {t.logo_url && <img src={`${backend}${t.logo_url}`} alt="" className="h-14 w-auto max-w-[160px] object-contain" data-testid="docket-logo" />}
            <div className="font-heading text-2xl font-black tracking-tight text-zinc-900" data-testid="docket-company">{t.name}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-xl font-black tracking-tight uppercase">Hire Delivery Docket</div>
            <div className="text-xs text-zinc-500 font-mono mt-1" data-testid="docket-number">No. {o.id.slice(-8).toUpperCase()}</div>
            <div className="text-xs text-zinc-500">Printed {fmtDate(localToday())}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <Block label="Customer" testid="docket-customer">{o.customer}</Block>
          <Block label="Delivery address / location" testid="docket-address">{o.customer_address}</Block>
          <Block label="Hire period" testid="docket-period">{`${fmtDate(o.start_date)} → ${fmtDate(o.end_date)}`}</Block>
          <Block label="PO number" testid="docket-po">{o.po_received ? o.po_number || "Received" : "Not received"}</Block>
          {o.branch_transfer && <Block label="Branch transfer" testid="docket-branch-transfer">{`Yes — to ${o.transfer_to} (no hire charge)`}</Block>}
          <Block label="Transport" testid="docket-transport">{TRANSPORT[o.transport] || ""}</Block>
          <Block label="Transport notes" testid="docket-transport-notes">{o.transport_notes}</Block>
        </div>

        <table className="w-full text-sm border border-zinc-300" data-testid="docket-equipment">
          <thead>
            <tr className="bg-zinc-100 text-left text-[10px] uppercase tracking-wider text-zinc-600">
              <th className="px-3 py-2">Asset ID</th><th className="px-3 py-2">Equipment</th>
              <th className="px-3 py-2">Serial no.</th><th className="px-3 py-2">WLL</th><th className="px-3 py-2 text-right">Qty</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-zinc-200 align-top">
              <td className="px-3 py-2 font-mono font-bold">{e.asset_id || "—"}</td>
              <td className="px-3 py-2">
                <div className="flex items-start gap-3">
                  {e.photo_id && <EquipmentThumb equipmentId={e.id} photoId={e.photo_id} className="w-16 h-16" testid="docket-equipment-photo" />}
                  <div>
                    <div className="font-semibold">{o.equipment_name}</div>
                    {(e.make_model || e.description) && <div className="text-xs text-zinc-500">{[e.make_model, e.description].filter(Boolean).join(" — ")}</div>}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2">{e.serial_number || "—"}</td>
              <td className="px-3 py-2">{e.wll || "—"}</td>
              <td className="px-3 py-2 text-right font-bold">{o.quantity}</td>
            </tr>
          </tbody>
        </table>

        <Block label="Accessories included" testid="docket-accessories">{e.accessories}</Block>

        <div className="border border-zinc-300 p-4 flex flex-wrap justify-between gap-4 text-sm" data-testid="docket-pricing">
          <span>Rate: <strong>{money(o.unit_price)}</strong> per {String(o.unit_label || "").replace("(s)", "")} × {o.units} {o.unit_label} × {o.quantity} unit(s)</span>
          <span className="font-heading text-lg font-black">Total {money(o.total_cost)}</span>
        </div>

        {o.note && <Block label="Notes" testid="docket-note">{o.note}</Block>}

        <div className="grid grid-cols-2 gap-10 pt-4">
          <SignLine label="Delivered by" />
          <SignLine label="Received by (customer)" />
        </div>
        <p className="text-[11px] text-zinc-500">Customer confirms the equipment and accessories listed were received in good working order.</p>
      </div>
    </div>
  );
}
